import { makeAutoObservable, runInAction } from "mobx";
import { apiClient } from "../utils/apiClient";
import { TLodging, TLoginResponse, TUser } from "../types/types";

export interface AuthStoreInterface {
  user: TUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

class AuthStore implements AuthStoreInterface {
  user: TUser | null = null;
  isAuthenticated = false;
  isLoading = false;
  error: string | null = null;
  establishmentId: number = 0;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Get lodgings from localStorage (read-only, not observable)
   */
  getLodgings(): TLodging[] {
    const cached = localStorage.getItem("lodgings");
    return cached ? JSON.parse(cached) : [];
  }

  async login(email: string, password: string): Promise<void> {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const response = await apiClient.post<TLoginResponse>("/auth/login", {
        email,
        password,
      });

      const { token, user, establishment } = response.data;
      const lodgings = establishment.lodgings || [];

      lodgings.forEach((lodging) => {
        try {
          if (lodging.pricing_structure) {
            lodging.pricing_structure_json = JSON.parse(
              lodging.pricing_structure,
            );
            delete lodging.pricing_structure; // Remove the original string if you only want the parsed object
          }
        } catch (err) {
          console.error(
            `Error parsing pricing structure for lodging ${lodging.id}:`,
            err,
          );
        }
      });

      runInAction(() => {
        this.user = user;
        this.isAuthenticated = true;
        this.establishmentId = establishment.id ?? 0;
        this.isLoading = false;
      });

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("establishmentId", `${establishment.id ?? 0}`);
      localStorage.setItem("lodgings", JSON.stringify(lodgings));
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Login failed";
        this.isAuthenticated = false;
        this.isLoading = false;
      });
    }
  }

  logout(): void {
    this.user = null;
    this.isAuthenticated = false;
    this.error = null;
    this.establishmentId = 0;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("establishmentId");
    localStorage.removeItem("lodgings");
  }

  async checkAuth(): Promise<void> {
    runInAction(() => {
      this.isLoading = true;
    });

    try {
      const token = localStorage.getItem("token");
      const establishmentId = localStorage.getItem("establishmentId");

      if (!token || !establishmentId) {
        this.logout();
        runInAction(() => {
          this.isLoading = false;
        });
        return;
      }

      // Verify token with backend
      const response = await apiClient.post("/admin/auth-verify");

      if (response.status !== 200) {
        this.logout();
      } else {
        runInAction(() => {
          this.isAuthenticated = true;
          this.isLoading = false;
        });
      }
    } catch (err) {
      // Token is invalid or expired
      runInAction(() => {
        // this.isAuthenticated = false;
        this.isLoading = false;
      });
      this.logout();
    }
  }

  getEstablishmentId(): number | null {
    const establishmentId = localStorage.getItem("establishmentId");
    return establishmentId ? parseInt(establishmentId) : null;
  }

  getAuthUser(): TUser | null {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  } 
  
}

export const authStore = new AuthStore();
